import {
  Activity,
  CheckCircle2,
  FlaskConical,
  Network,
  Sparkles
} from "lucide-react";
import type {
  ProposalLineageProjection,
  RunProjection
} from "@roboscientist/contracts";
import {
  buildAssessmentTrajectory,
  buildCumulativeRunPath,
  buildPrimaryMetricChanges,
  buildRunFieldEntries
} from "./researchLineageEvidence.js";
import { lineageNodeDomId } from "./researchLineageLayout.js";

const outcomeLabel = {
  passed: "Passed",
  failed: "Failed",
  improved: "Improved",
  not_improved: "Not improved",
  inconclusive: "Inconclusive",
  invalid: "Invalid evidence"
} as const;

const formatMetricValue = (value: number): string =>
  Math.abs(value) >= 1_000 || (Math.abs(value) > 0 && Math.abs(value) < .001)
    ? value.toExponential(3)
    : value.toLocaleString("en-US", { maximumFractionDigits: 4 });

function AssessmentTrajectory({ lineage, domainLineage }: { lineage: ProposalLineageProjection; domainLineage?: ProposalLineageProjection }) {
  const points = buildAssessmentTrajectory(lineage.nodes);
  const domainPoints = domainLineage ? buildAssessmentTrajectory(domainLineage.nodes) : points;
  const outcomes = Object.keys(outcomeLabel) as Array<keyof typeof outcomeLabel>;
  const width = 700;
  const height = 164;
  const padX = 42;
  const padY = 23;
  const scores = [0, ...domainPoints.map((point) => point.score)];
  const maxScore = Math.max(1, ...scores);
  const y = (score: number) => padY + (maxScore - score) / maxScore * (height - padY * 2);
  const ticks = [...new Set([maxScore, Math.ceil(maxScore / 2), 0])].sort((left, right) => right - left);
  const plotted = points.map((point, index) => ({
    ...point,
    x: padX + (index + 1) / (domainPoints.length + 1) * (width - padX * 2),
    y: y(point.score)
  }));
  const path = plotted.reduce((value, point) => `${value} H ${point.x.toFixed(1)} V ${point.y.toFixed(1)}`, `M ${padX} ${y(0).toFixed(1)}`)
    + (plotted.length && points.length === domainPoints.length ? ` H ${width - padX}` : "");

  return <section className="lineage-assessment-card">
    <header>
      <div><span>AGENT REWARD</span><strong>Cumulative confirmed improvements</strong></div>
      <div className="lineage-assessment-counts">
        <span className="is-reward">Reward {points.at(-1)?.score ?? 0}</span>
        {outcomes.map((outcome) => {
          const count = points.filter((point) => point.assessment.outcome === outcome).length;
          return count ? <span key={outcome} className={`is-${outcome}`}>{count} {outcomeLabel[outcome].toLowerCase()}</span> : null;
        })}
        <span>{points.length} assessed</span>
      </div>
    </header>
    <div className="lineage-assessment-chart">
      <div className="lineage-assessment-plot">
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label={`${points.length} explicit Agent assessments; cumulative reward ${points.at(-1)?.score ?? 0}; reward never decreases`}>
          {ticks.map((score) => <g key={score}>
            <line x1={padX} x2={width - padX} y1={y(score)} y2={y(score)} />
          </g>)}
          {points.length ? <path className="lineage-assessment-line" d={path} /> : null}
        </svg>
        {ticks.map((score) => <span key={score} className="lineage-assessment-axis-label" style={{ position: "absolute", left: 5, top: `${y(score) / height * 100}%`, transform: "translateY(-50%)", font: "10px var(--mono)", color: "var(--muted)" }}>{score}</span>)}
        {plotted.map((point) => {
          const targetId = lineageNodeDomId(point.nodeKey);
          const edgeClass = point.x < 140 ? "is-left-edge" : point.x > width - 140 ? "is-right-edge" : "";
          const verticalClass = point.y < height / 2 ? "tooltip-below" : "";
          return <a
            key={point.id}
            href={`#${targetId}`}
            className={`lineage-assessment-point lineage-assessment-point--${point.assessment.outcome} ${edgeClass} ${verticalClass}`}
            style={{ left: `${point.x / width * 100}%`, top: `${point.y / height * 100}%` }}
            aria-label={`Open node ${point.nodeTitle}: ${outcomeLabel[point.assessment.outcome]}, reward +${point.delta}`}
            onClick={(event) => {
              event.preventDefault();
              const target = document.getElementById(targetId);
              if (!(target instanceof HTMLButtonElement)) return;
              target.click();
              target.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
              window.requestAnimationFrame(() => target.focus({ preventScroll: true }));
            }}
          >
            <i aria-hidden="true" />
            <span className="lineage-assessment-point__tooltip" role="tooltip">
              <strong>{point.nodeTitle}</strong>
              <span>{outcomeLabel[point.assessment.outcome]}</span>
              <small>Reward +{point.delta} · cumulative {point.score}</small>
              <time>{new Date(point.assessment.assessedAt).toLocaleString("en-US")}</time>
              <em>Open node →</em>
            </span>
          </a>;
        })}
        {!points.length ? <div className="lineage-assessment-empty"><CheckCircle2 size={18} /><strong>No explicit assessments yet</strong><span>Metrics and completed Runs remain evidence; they do not create an “improved” reward.</span></div> : null}
      </div>
      <footer><span>All non-improved outcomes +0 · no negative reward</span><strong>Improved +1 · reward never decreases</strong></footer>
    </div>
  </section>;
}

function CumulativeRunHistory({ runs, domainRuns }: { runs: RunProjection[]; domainRuns?: RunProjection[] }) {
  const chart = buildCumulativeRunPath(runs, 700, 164, domainRuns);
  const metrics = buildPrimaryMetricChanges(runs);
  return <section className="lineage-volume-card">
    <header><div><span>RUN VOLUME</span><strong>Cumulative observed Run count</strong></div><div className="lineage-volume-meta"><Activity size={14} /><span>{runs.length} lifecycle records · curve is not a metric</span></div></header>
    <div className="lineage-volume-chart">
      <svg viewBox="0 0 700 164" preserveAspectRatio="none" role="img" aria-label={`Cumulative observed Run count: ${runs.length}; this curve is not metric performance`}>
        <defs><linearGradient id="lineage-volume-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="var(--color-accent-fg)" stopOpacity=".3" /><stop offset="1" stopColor="var(--color-accent-fg)" stopOpacity="0" /></linearGradient></defs>
        {[32, 64, 96, 128].map((lineY) => <line key={lineY} x1="0" x2="700" y1={lineY} y2={lineY} />)}
        {chart.area ? <path d={chart.area} className="lineage-volume-area" /> : null}
        {chart.line ? <path d={chart.line} className="lineage-volume-line" /> : null}
      </svg>
      {!chart.line ? <div className="lineage-volume-empty">No Run timestamps observed yet.</div> : null}
      <footer><span>{chart.first} · first observed</span><span>time → cumulative Run count</span><strong>{chart.last} · latest observed</strong></footer>
    </div>
    <div className="lineage-volume-metrics">
      <span>PARSED PRIMARY METRIC CHANGES</span>
      {metrics.length ? <div>{metrics.map((metric) => {
        const delta = formatMetricValue(metric.delta);
        return <article key={metric.name} title={`${metric.name}: ${formatMetricValue(metric.firstValue)} to ${formatMetricValue(metric.lastValue)} across ${metric.observationCount} observations. Direction is descriptive; the node rule determines whether it is better.`}>
          <code>{metric.name}</code>
          <strong>{formatMetricValue(metric.firstValue)} → {formatMetricValue(metric.lastValue)}</strong>
          <small>Δ {metric.delta > 0 ? "+" : ""}{delta} · {metric.observationCount} observation{metric.observationCount === 1 ? "" : "s"}</small>
        </article>;
      })}</div> : <p>No primary metric values were parsed from these Run manifests.</p>}
    </div>
  </section>;
}

export function ResearchLineageEvidencePanel({
  lineage,
  runs,
  replay
}: {
  lineage: ProposalLineageProjection;
  runs: RunProjection[];
  replay?: { lineage: ProposalLineageProjection; runs: RunProjection[]; playing: boolean; complete: boolean };
}) {
  const field = buildRunFieldEntries(lineage, runs);
  const assessed = lineage.nodes.filter((node) => node.latestAssessment).length;
  const active = field.filter((entry) => entry.lifecycle === "active").length;
  const bound = field.filter((entry) => entry.binding === "bound").length;
  return <section className="lineage-evidence-overview">
    <header className="lineage-evidence-overview__header">
      <div><span className="panel__eyebrow"><Sparkles size={12} /> PROPOSAL EVIDENCE</span><h2>Observe Assess Select Evolve</h2><p>Formal lineage stays separate from observed Run history. Scientific outcomes below come only from explicit Agent assessments.</p></div>
      <div className={`lineage-evidence-state ${replay ? (replay.playing ? "is-live" : "") : active ? "is-live" : ""}`}><i /><span>{replay && !replay.complete ? (replay.playing ? "Evidence replay in progress" : "Evidence replay paused") : active ? `${active} Run${active === 1 ? "" : "s"} active` : "Evidence index reconciled"}</span></div>
    </header>
    <div className="lineage-evidence-kpis">
      <article className="is-primary"><span>Formal nodes</span><strong>{lineage.nodes.length}</strong><small><Network size={12} /> declared causal steps</small></article>
      <article><span>Explicitly assessed</span><strong>{assessed}</strong><small><CheckCircle2 size={12} /> Agent-authored outcomes</small></article>
      <article><span>Bound evidence Runs</span><strong>{bound}</strong><small><FlaskConical size={12} /> linked to formal nodes</small></article>
      <article><span>Unbound Runs</span><strong>{lineage.unassignedRuns.length}</strong><small><Activity size={12} /> retained without invention</small></article>
    </div>
    <AssessmentTrajectory lineage={lineage} domainLineage={replay?.lineage} />
    <CumulativeRunHistory runs={runs} domainRuns={replay?.runs} />
  </section>;
}
